/**
 * 网站服务
 * 处理与网站相关的所有API调用
 */

import { get, post, put, del } from './apiClient';
import { getAuthDataFromStorage } from './authService';
import logger from '../utils/logger';

// 网站相关API端点
const API_ENDPOINTS = {
  NAVS: '/navs',
  NAV: (id) => `/navs/${id}`,
  NAV_WEBSITES: (navId) => `/navs/${navId}/websites`,
  WEBSITE: (navId, websiteId) => `/navs/${navId}/websites/${websiteId}`
};

/**
 * 检查用户是否已登录并获取 token
 * @returns {Object|null} 包含 token 的对象或 null
 */
const getAuthToken = () => {
  const authData = getAuthDataFromStorage();
  return authData && authData.token ? { token: authData.token } : null;
};

/**
 * 检查用户是否已登录
 * @returns {boolean} 用户是否已登录
 */
const isUserLoggedIn = () => {
  const authData = getAuthDataFromStorage();
  return !!authData && !!authData.token;
};

/**
 * 获取所有导航菜单
 * @returns {Promise<Array>} 导航菜单数组
 */
export const fetchNavs = async () => {
  if (!isUserLoggedIn()) {
    logger.log('[Website] 用户未登录，不从API获取导航菜单列表');
    return null;
  }
  
  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('[Website] 获取认证 token 失败');
      return null;
    }
    
    const response = await get(API_ENDPOINTS.NAVS, token);
    return response.data || [];
  } catch (error) {
    logger.error('[Website] 获取导航菜单列表失败:', error);
    return null;
  }
};

/**
 * 获取指定导航菜单下的所有网站
 * @param {string} navId 导航菜单ID
 * @returns {Promise<Array>} 该导航菜单下的网站数组
 */
export const fetchNavWebsites = async (navId) => {
  if (!isUserLoggedIn()) {
    logger.log(`[Website] 用户未登录，不从API获取导航菜单 ${navId} 的网站列表`);
    return null;
  }
  
  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('[Website] 获取认证 token 失败');
      return null;
    }
    
    const response = await get(API_ENDPOINTS.NAV_WEBSITES(navId), token);
    return response.data || [];
  } catch (error) {
    logger.error(`[Website] 获取导航菜单 ${navId} 的网站列表失败:`, error);
    return null;
  }
};

/**
 * 在指定导航菜单下添加新网站
 * @param {string} navId 导航菜单ID
 * @param {Object} website 要添加的网站数据
 * @returns {Promise<Object>} 添加后的网站数据，包含服务端生成的ID
 */
export const addWebsite = async (navId, website) => {
  if (!isUserLoggedIn()) {
    logger.log(`[Website] 用户未登录，不向API添加导航菜单 ${navId} 的网站`);
    return null;
  }
  
  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('[Website] 获取认证 token 失败');
      return null;
    }
    
    const response = await post(API_ENDPOINTS.NAV_WEBSITES(navId), website, token);
    
    // 检查响应是否成功
    if (response.success === false) {
      // 返回错误信息
      return { error: response.error || '添加网站失败' };
    }
    
    return response.data; // 返回包含服务端ID的对象
  } catch (error) {
    logger.error(`[Website] 在导航菜单 ${navId} 下添加网站失败:`, error);
    return { error: error.message || '网络请求失败' };
  }
};

/**
 * 更新指定导航菜单下的网站
 * @param {string} navId 导航菜单ID
 * @param {string} websiteId 网站ID
 * @param {Object} updates 要更新的网站属性
 * @returns {Promise<Object>} 更新后的网站数据
 */
export const updateWebsite = async (navId, websiteId, updates) => {
  if (!isUserLoggedIn()) {
    logger.log(`[Website] 用户未登录，不向API更新导航菜单 ${navId} 下的网站 ${websiteId}`);
    return null;
  }
  
  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('[Website] 获取认证 token 失败');
      return null;
    }
    
    const response = await put(API_ENDPOINTS.WEBSITE(navId, websiteId), updates, token);
    return response.data;
  } catch (error) {
    logger.error(`[Website] 更新导航菜单 ${navId} 下的网站 ${websiteId} 失败:`, error);
    return null;
  }
};

/**
 * 从指定导航菜单中删除网站
 * @param {string} navId 导航菜单ID
 * @param {string} websiteId 网站ID
 * @returns {Promise<boolean>} 删除成功返回true
 */
export const deleteWebsite = async (navId, websiteId) => {
  if (!isUserLoggedIn()) {
    logger.log(`[Website] 用户未登录，不向API删除导航菜单 ${navId} 中的网站 ${websiteId}`);
    return null;
  }
  
  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('[Website] 获取认证 token 失败');
      return null;
    }
    
    await del(API_ENDPOINTS.WEBSITE(navId, websiteId), token);
    return true;
  } catch (error) {
    logger.error(`[Website] 从导航菜单 ${navId} 中删除网站 ${websiteId} 失败:`, error);
    return null;
  }
};

/**
 * 更新导航菜单下所有网站的排序
 * @param {string} navId 导航菜单ID
 * @param {Array} websites 重新排序后的该导航菜单下的完整网站数组
 * @returns {Promise<Array>} 更新后的网站数组
 */
export const reorderWebsites = async (navId, websites) => {
  if (!isUserLoggedIn()) {
    logger.log(`[Website] 用户未登录，不向API同步导航菜单 ${navId} 下的网站顺序`);
    return null;
  }
  
  try {
    const token = await getAuthToken();
    if (!token) {
      logger.error('[Website] 获取认证 token 失败');
      return null;
    }
    
    const endpoint = API_ENDPOINTS.NAV_WEBSITES(navId);

    
    const response = await put(endpoint, { websites }, token);
    logger.log('[Website] 请求成功，服务端返回:', response);
    
    return response.data || [];
  } catch (error) {
    logger.error('[Website] 重新排序导航菜单失败:', error);
    logger.error('[Website] 错误详情:', error.message, error.statusCode);
    logger.error('[Website] 请求URL和参数:', `${API_ENDPOINTS.NAV_WEBSITES(navId)}`, { websites });
    
    if (error.statusCode === 404) {
      logger.error('[Website] 404错误，可能是API路径不正确。请检查后端路由配置和前端API端点定义是否匹配');
      logger.log('[Website] 当前后端可用路径参考:');
    }
    
    return null;
  }
};

export default {
  fetchNavs,
  fetchNavWebsites,
  addWebsite,
  updateWebsite,
  deleteWebsite,
  reorderWebsites
}; 